import TasksService from 'src/services/tasks'
import OrganizationService from 'src/services/organization'
import taskHierarchyService from 'src/services/taskHierarchy'
import ActionPlanTask from 'src/models/ActionPlanTask.model'
import { useUsersStore } from 'src/stores/index.js'
import initialState from './state'
import { isFlagEnabled } from 'src/offline/flags'
import { isReallyOnlineUi } from 'src/services/connectivity'
import { catalogs as offlineCatalogs } from 'src/offline/domain'
import { notifyConnectionError } from 'src/api/responser'

// Config de los fetch de catálogo del diálogo de tarea (SOLO camino flag-ON): sin toast del
// interceptor (el helper decide: cache servido = silencio; sin cache = UN toast throttled) y
// timeout propio MÁS CORTO que el default global de axios (boot/axios.js, 30s desde el fix de
// pestañas en blanco): 15s es holgado para backend LAN lento, pero corto para no colgar el
// diálogo esperando el default general.
const CATALOG_FETCH_CONFIG = { suppressConnectionToast: true, timeout: 15000 }
let fetchFilteredRequestId = 0

/**
 * Read-through de un catálogo del diálogo de tarea (patrón ISS-843, espejo de
 * resolveSignatureRolesWithCache): online → fetch fresco + cacheo best-effort; fallo u
 * offline → último cache. `isValid` sigue siendo necesario como cinturón (un 200 con body
 * inesperado/vacío no debe pisar el cache), aunque desde el fix de timeouts en
 * api/responser.js un error sin `.response` (timeout/network) YA RECHAZA correctamente en vez de
 * resolver como si fuera éxito — ese caso hoy cae al `catch` de abajo y sirve cache igual, sin
 * pasar por este predicado.
 * Flag OFF ⇒ fetch directo con config vacía = comportamiento byte-idéntico al actual.
 *
 * TRADE-OFF documentado: si el fetch falla/vence CON cache previo, se sirve el cache SIN señal
 * de error — para el flujo de captura eso es lo correcto (el video: red rota ≠ selects vacíos),
 * pero una pantalla admin que refresca tras un write (HierarchyConfiguration) puede ver datos
 * pre-write hasta que la red se recupere. Aceptado: ese flujo es de oficina/online; el fetch
 * exitoso siguiente re-sincroniza el cache.
 */
async function readThroughCatalog ({ fetchRemote, cacheGet, cachePut = null, isValid }) {
  if (!isFlagEnabled('offline_mode_enabled')) return fetchRemote({})
  if (await isReallyOnlineUi()) {
    try {
      const body = await fetchRemote(CATALOG_FETCH_CONFIG)
      if (isValid(body)) {
        if (cachePut) cachePut(body) // best-effort, sin await: no frena la UI
        return body
      }
      const cached = await cacheGet()
      return cached != null ? cached : body
    } catch (err) {
      const cached = await cacheGet()
      if (cached != null) return cached
      // Sin cache que amortigüe: restaurar la señal visible que el interceptor suprimió
      // (throttled: N catálogos fallando en ráfaga = UN toast) y el error-path de siempre.
      notifyConnectionError()
      throw err
    }
  }
  return cacheGet() // offline franco: cero requests, cero toasts; null si nunca hubo cache
}

// orgId del usuario autenticado (misma cadena que el scope resolver de boot/offline.js) para
// scopear los caches por organización en device compartido.
function currentOrgId () {
  const u = useUsersStore().current
  if (!u) return null
  return (u.organization && u.organization.id) || u.franchise_organization_id || u.organization_id || null
}

export default {
  resetTaskQuery () {
    this.taskQuery = initialState().taskQuery
  },
  updateTaskRole (role) {
    this.taskQuery.role = role
    this.taskQuery.users = []
  },
  updateTaskUsers (users) {
    this.taskQuery.users = users
  },
  updateTaskLocations (locations) {
    this.taskQuery.location_id = locations
    this.taskQuery.users = []
  },
  updateTaskEndDate (endDate) {
    this.taskQuery.endDate = endDate
  },
  updateTaskQuery (payload) {
    this.taskQuery = {
      ...this.taskQuery,
      ...payload
    }
  },
  updateTaskName (title) {
    this.taskQuery.title = title
  },
  updateTaskPriority (priority) {
    switch (priority) {
      case 'Alta':
        priority = 3
        break
      case 'Media':
        priority = 2
        break
      case 'Baja':
        priority = 1
        break
      default:
        priority = null
        break
    }
    this.taskQuery.priority = priority
  },
  updateTaskComment (comment) {
    this.taskQuery.comment = comment
  },
  updateTaskPicture (picture) {
    this.taskQuery.picture = picture
  },
  updateTaskCategory (category) {
    this.taskQuery.category = category
  },
  updateTaskCategory2 (category) {
    this.taskQuery.category2 = category
  },
  updateEnforceEvidence (value) {
    this.taskQuery.enforceEvidence = value
  },
  updateShortName (shortName) {
    this.taskQuery.short_name = shortName
  },
  createTask (task) {
    const record = {
      role: task.role,
      assigned_to: task.users,
      location_id: task.location_id[0],
      end_date: task.endDate ? task.endDate : null,
      priority: task.priority ? task.priority : null,
      title: task.title ? task.title : null,
      tag: task.category ? task.category : null,
      tag2: task.category2 ? task.category2 : null,
      area_id: task.area_id ? task.area_id : null,
      enforce_evidence: task.enforceEvidence ? task.enforceEvidence : null,
      short_name: task.short_name ? task.short_name : null,
      hierarchy_site_id: task.hierarchy_site_id ? task.hierarchy_site_id : null,
      hierarchy_subcategory_id: task.hierarchy_subcategory_id ? task.hierarchy_subcategory_id : null
    }
    task.pictures !== null ? record.pictures = task.pictures : record.pictures = []

    if (task.comment !== null) record.comment = task.comment
    if (task.state !== null) record.state = task.state
    if (task.ocurrenceId !== null) record.ocurrence_id = task.ocurrenceId
    if (task.formId !== null) record.form_id = task.formId
    if (task.itemId !== null) record.item_id = task.itemId

    const promise = TasksService.createTask(record)

    return promise
  },
  createMultipleTasks (task) {
    const record = {
      role: task.role,
      created_by: task.created_by,
      assigned_to: task.users,
      location_id: task.location_id,
      end_date: task.endDate ? task.endDate : null,
      priority: task.priority ? task.priority : null,
      title: task.title ? task.title : null,
      tag: task.category ? task.category : null,
      tag2: task.category2 ? task.category2 : null,
      area_id: task.area_id ? task.area_id : null,
      enforce_evidence: task.enforceEvidence ? task.enforceEvidence : null,
      short_name: task.short_name ? task.short_name : null,
      hierarchy_site_id: task.hierarchy_site_id ? task.hierarchy_site_id : null,
      hierarchy_subcategory_id: task.hierarchy_subcategory_id ? task.hierarchy_subcategory_id : null
    }
    task.pictures !== null ? record.pictures = task.pictures : record.pictures = []

    if (task.comment !== null) record.comment = task.comment

    const promise = TasksService.createMultipleTasks(record)

    return promise
  },
  fetchFiltered (options) {
    const payload = {
      limit: 15,
      page: 1,
      ...options
    }

    const requestId = ++fetchFilteredRequestId
    const promise = TasksService.fetchFiltered(payload)

    promise
      .then(response => {
        if (requestId !== fetchFilteredRequestId) return
        this.tasks = response.data.map(task => {
          return new ActionPlanTask(task)
        })
        this.pagination = {
          page: payload.page,
          rowsPerPage: payload.limit,
          rowsNumber: response.total
        }
      })
      .catch(error => {
        console.error(error)
      })

    return promise
  },
  selectAllUsersTasks () {
    const usersStore = useUsersStore()
    this.taskQuery.users = usersStore.users.map(user => user.id)
  },
  async fetchTags (organizationId) {
    try {
      const response = await readThroughCatalog({
        fetchRemote: cfg => OrganizationService.getGeneralSettings(organizationId, 'ap_categories', cfg),
        cacheGet: () => offlineCatalogs.getCachedOrgConfig(organizationId, 'ap_categories'),
        cachePut: body => offlineCatalogs.cacheOrgConfig(organizationId, 'ap_categories', body),
        isValid: b => !!b && typeof b === 'object' && 'data' in b
      })
      if (response.data) {
        this.tags = JSON.parse(response.data.data)
        this.tags3 = JSON.parse(response.data.data)
      } else {
        this.tags = []
        this.tags3 = []
      }
    } catch (err) {
      console.error(err)
      this.tags = []
      this.tags3 = []
    }
  },
  async fetchTags2 (organizationId) {
    try {
      const response = await readThroughCatalog({
        fetchRemote: cfg => OrganizationService.getGeneralSettings(organizationId, 'ap_categories2', cfg),
        cacheGet: () => offlineCatalogs.getCachedOrgConfig(organizationId, 'ap_categories2'),
        cachePut: body => offlineCatalogs.cacheOrgConfig(organizationId, 'ap_categories2', body),
        isValid: b => !!b && typeof b === 'object' && 'data' in b
      })
      if (response.data) {
        this.tags2 = JSON.parse(response.data.data)
        this.tags4 = JSON.parse(response.data.data)
      } else {
        this.tags2 = []
        this.tags4 = []
      }
      return response
    } catch (err) {
      console.error(err)
      this.tags2 = []
      this.tags4 = []
      return { data: null }
    }
  },
  reassignTask (payload) {
    return TasksService.reassignTask(payload)
  },
  changeStatus (payload) {
    return TasksService.changeStatus(payload)
  },
  findTaskById (payload) {
    const promise = TasksService.findById(payload)

    promise.then(data => {
      this.task = data
    })

    return promise
  },
  deselectUsersTasks () {
    this.taskQuery.users = []
  },
  updatePagination (pagination) {
    this.pagination = pagination
  },
  fetchFilteredAreas (categoryName) {
    const orgId = currentOrgId()
    return readThroughCatalog({
      fetchRemote: cfg => TasksService.fetchFilteredAreas(categoryName, cfg),
      cacheGet: () => offlineCatalogs.getCachedFilteredAreas(orgId, categoryName),
      cachePut: list => offlineCatalogs.cacheFilteredAreas(orgId, categoryName, list),
      isValid: Array.isArray
    })
  },
  // ── Jerarquía del diálogo de tarea (TaskHierarchySelector) con fallback offline ──
  // sites/subcategories NO tienen cachePut: c_hierarchies la puebla/revalida el pull Tier B
  // del sync-worker (TTL 12h); aquí solo se lee. Categorías-por-sitio sí es read-through
  // (no hay tabla c_*; on-demand en meta — pre-calentarlas sería fan-out por sitio).
  fetchHierarchySites () {
    return readThroughCatalog({
      fetchRemote: cfg => taskHierarchyService.getSites({}, cfg),
      cacheGet: () => offlineCatalogs.getCachedSites(),
      isValid: b => !!b && Array.isArray(b.data)
    })
  },
  fetchHierarchySubcategories () {
    return readThroughCatalog({
      fetchRemote: cfg => taskHierarchyService.getSubcategories({}, cfg),
      cacheGet: () => offlineCatalogs.getCachedSubcategories(),
      isValid: b => !!b && Array.isArray(b.data)
    })
  },
  fetchSiteCategories (siteId) {
    return readThroughCatalog({
      fetchRemote: cfg => taskHierarchyService.getCategoriesForSite(siteId, cfg),
      cacheGet: () => offlineCatalogs.getCachedSiteCategories(siteId),
      cachePut: body => offlineCatalogs.cacheSiteCategories(siteId, body),
      isValid: b => !!b && Array.isArray(b.data)
    })
  },
  async fetchAreasForCategories (categories) {
    if (!categories || categories.length === 0) return this.tags2
    const validCategories = categories.filter(cat => cat && cat.trim() !== '')
    if (validCategories.length === 0) return this.tags2

    const categoriesWithAreas = []

    for (const category of validCategories) {
      try {
        const areas = await this.fetchFilteredAreas(category)

        if (areas && areas.length > 0) categoriesWithAreas.push(category)
      } catch (error) {
        console.error(`Category "${category}" has no related areas or an error occurred:`, error)
      }
    }

    if (categoriesWithAreas.length === 0) return this.tags2
    if (categoriesWithAreas.length === 1) {
      return this.fetchFilteredAreas(categoriesWithAreas[0])
    }

    const areaPromises = categoriesWithAreas.map(category => this.fetchFilteredAreas(category))
    const allAreas = await Promise.all(areaPromises)
    // fetchFilteredAreas puede resolver null (offline sin cache): solo se aplanan arrays reales.
    const flatAreas = allAreas.filter(Array.isArray).flat()
    return [...new Set(flatAreas)]
  }
}
